import * as React from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { MailCheck } from 'lucide-react';
import {
  EMAIL_OPTIN_SOURCE_KINDS,
  EMAIL_OPTIN_SOURCE_LABELS,
  type EmailOptinSourceKind,
  type EmailSuppressionReason,
} from '@aimmo/contracts';
import {
  Badge,
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  Label,
  NativeSelect,
  Skeleton,
  Textarea,
  toast,
} from '@aimmo/ui';
import { api, assertOk } from '../lib/api.js';
import { useAbility } from '../lib/abilities.js';
import { formatDateTime } from '../lib/format.js';
import { queryKeys } from '../lib/queries.js';

const SUPPRESSION_LABELS: Record<EmailSuppressionReason, string> = {
  bounce: 'non recapitabile',
  complaint: 'segnalata come spam',
};

/**
 * A contact's email standing, on the contact itself (M10 / P3).
 *
 * The only place email consent comes from. There is no switch that grants it:
 * the agent records where the person asked, the person receives a link, and
 * consent exists once they confirm. The card says which of those states the
 * contact is in, and never lets an agent skip the middle one.
 */
export function ContactEmailCard({ contactId }: { contactId: string }) {
  const statusKey = queryKeys.contactEmailStatus(contactId);
  const ability = useAbility();
  const status = useQuery({
    queryKey: statusKey,
    queryFn: async () =>
      assertOk(await api.email.getContactEmailStatus({ params: { id: contactId } })).body,
  });

  if (status.isPending) return <Skeleton className="h-16" />;
  if (!status.data)
    return <p className="text-secondary text-muted">Stato email non disponibile.</p>;

  const data = status.data;
  const open = data.openRequests.find((request) => !request.expired);

  return (
    <div className="grid gap-3">
      <div className="flex flex-wrap items-center gap-2">
        {data.consent.granted ? (
          <Badge tone="accent">Consenso confermato</Badge>
        ) : data.consent.revokedAt ? (
          <Badge tone="warning">Disiscritto</Badge>
        ) : open ? (
          <Badge tone="neutral">In attesa di conferma</Badge>
        ) : (
          <Badge tone="neutral">Nessun consenso</Badge>
        )}
        <span className="text-secondary text-muted">
          {data.consent.granted && data.consent.grantedAt
            ? `dal ${formatDateTime(data.consent.grantedAt)}`
            : data.consent.revokedAt
              ? `dal ${formatDateTime(data.consent.revokedAt)}`
              : open
                ? `link inviato a ${open.address}, valido fino al ${formatDateTime(open.expiresAt)}`
                : 'le campagne email non lo raggiungono'}
        </span>
      </div>

      {data.addresses.some((address) => address.suppressed) && (
        <ul className="grid gap-1">
          {data.addresses
            .filter((address) => address.suppressed)
            .map((address) => (
              <li key={address.address} className="flex flex-wrap items-baseline gap-2">
                <span className="text-body text-ink">{address.address}</span>
                <Badge tone="warning">{SUPPRESSION_LABELS[address.suppressed!]}</Badge>
              </li>
            ))}
        </ul>
      )}

      {!data.consent.granted && ability.can('update', 'Contact') && (
        <OptinDialog
          contactId={contactId}
          addresses={data.addresses.map((address) => address.address)}
          resend={!!open}
        />
      )}
    </div>
  );
}

function OptinDialog({
  contactId,
  addresses,
  resend,
}: {
  contactId: string;
  addresses: string[];
  resend: boolean;
}) {
  const queryClient = useQueryClient();
  const [open, setOpen] = React.useState(false);
  const [address, setAddress] = React.useState(addresses[0] ?? '');
  const [sourceKind, setSourceKind] = React.useState<EmailOptinSourceKind>('in_agency');
  const [sourceNote, setSourceNote] = React.useState('');

  const send = useMutation({
    mutationFn: async () =>
      assertOk(
        await api.email.requestEmailOptin({
          params: { id: contactId },
          body: { address, sourceKind, sourceNote },
        }),
      ),
    onSuccess: (result) => {
      queryClient.setQueryData(['contacts', contactId, 'email-status'], result.body);
      toast.success(`Email di conferma inviata a ${address}`);
      setOpen(false);
    },
    onError: (error: Error) => toast.error(error.message),
  });

  if (addresses.length === 0) {
    return (
      <p className="text-secondary text-muted">
        Aggiungi un indirizzo email ai recapiti per poter chiedere la conferma.
      </p>
    );
  }

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <div>
        <Button variant="secondary" size="sm" onClick={() => setOpen(true)}>
          <MailCheck /> {resend ? 'Reinvia la conferma' : 'Registra richiesta di iscrizione'}
        </Button>
      </div>
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Richiesta di iscrizione alle email</DialogTitle>
          <DialogDescription>
            Registra dove il contatto ha chiesto di ricevere le comunicazioni. Riceverà un’email con
            un link: il consenso vale solo quando lo conferma.
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-3">
          <div className="grid gap-1">
            <Label htmlFor="optin-address">Indirizzo</Label>
            <NativeSelect
              id="optin-address"
              value={address}
              onChange={(event) => setAddress(event.target.value)}
            >
              {addresses.map((option) => (
                <option key={option} value={option}>
                  {option}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="optin-source">Dove l’ha chiesto</Label>
            <NativeSelect
              id="optin-source"
              value={sourceKind}
              onChange={(event) => setSourceKind(event.target.value as EmailOptinSourceKind)}
            >
              {EMAIL_OPTIN_SOURCE_KINDS.map((kind) => (
                <option key={kind} value={kind}>
                  {EMAIL_OPTIN_SOURCE_LABELS[kind]}
                </option>
              ))}
            </NativeSelect>
          </div>

          <div className="grid gap-1">
            <Label htmlFor="optin-note">Nota</Label>
            <Textarea
              id="optin-note"
              rows={3}
              value={sourceNote}
              onChange={(event) => setSourceNote(event.target.value)}
              placeholder={sourceKind === 'other' ? 'Obbligatoria per «Altro»' : 'Facoltativa'}
            />
          </div>
        </div>

        <DialogFooter>
          <Button
            disabled={send.isPending || (sourceKind === 'other' && !sourceNote.trim())}
            onClick={() => send.mutate()}
          >
            Invia email di conferma
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
